import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, delay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CreateActivityPayload } from '../core/activity.model';
import { DemoModeService } from './demo-mode.service';
import { DemoStoreService } from './demo-store.service';
import { simulateCrawlDraft } from './demo-data';

/**
 * Demo mode's API: when the demo flag is on, requests to the Advocad API are
 * answered from the in-memory DemoStore — with ONE deliberate exception: the
 * public, auth-free crawler endpoints (/demo/*, /sources) pass through to the
 * real API so visitors can paste their own links and watch the real crawler
 * measure real reach. If that call fails (API down, offline), a simulated
 * draft stands in so the demo never breaks. Nothing stateful ever leaves the
 * browser. Registered FIRST in the interceptor chain, so the Auth0 token
 * interceptor (which would fail with no session) never sees these requests.
 * When demo mode is off this is a strict pass-through.
 */
@Injectable()
export class DemoApiInterceptor implements HttpInterceptor {
  private readonly _demo = inject(DemoModeService);
  private readonly _store = inject(DemoStoreService);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const base = environment.apiBaseUrl;
    if (!this._demo.active || !req.url.startsWith(base)) {
      return next.handle(req);
    }
    const path = req.url.slice(base.length).split('?')[0];
    return this._handle(req, path, next);
  }

  /**
   * Wrap a body as a slightly-delayed 200 so the UI's loading states show.
   * The body is deep-cloned: callers cache responses (shareReplay etc.), and
   * serving live store references would let later mutations double-count.
   */
  private _ok<T>(body: T, ms = 250): Observable<HttpEvent<unknown>> {
    return of(
      new HttpResponse({
        status: 200,
        body: body === undefined ? body : structuredClone(body),
      }),
    ).pipe(delay(ms));
  }

  private _handle(
    req: HttpRequest<unknown>,
    path: string,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const method = req.method;
    const body = req.body as Record<string, unknown> | null;

    // ── activities ──
    if (path === '/activities' && method === 'GET') {
      return this._ok(this._store.activities);
    }
    if (path === '/activities' && method === 'POST') {
      return this._ok(
        this._store.create(req.body as CreateActivityPayload),
        400,
      );
    }
    if (path === '/activities/stats/summary' && method === 'GET') {
      return this._ok(this._store.summary());
    }
    const readings = path.match(/^\/activities\/([^/]+)\/readings$/);
    if (readings && method === 'GET') {
      return this._ok(this._store.readings(readings[1]));
    }
    // Insights — must sit ABOVE the generic /activities/:id matcher so
    // /activities/:id/insights is not swallowed by it. The demo shows a small
    // fixed feed so the replica looks alive; nothing is persisted.
    const activityInsights = path.match(/^\/activities\/([^/]+)\/insights$/);
    if (activityInsights && method === 'GET') {
      return this._ok(this._store.insightsForActivity(activityInsights[1]));
    }
    if (path === '/insights' && method === 'GET') {
      return this._ok(this._store.insights());
    }
    if (path === '/analyze/run' && method === 'POST') {
      // A demo cannot run a real engine, so acknowledge without doing work.
      return this._ok({ skipped: true, skip_reason: 'disabled' }, 600);
    }
    const insightState = path.match(/^\/insights\/([^/]+)\/state$/);
    if (insightState && method === 'PATCH') {
      return this._ok({ _id: insightState[1], ...(req.body as object) });
    }
    const insight = path.match(/^\/insights\/([^/]+)$/);
    if (insight && method === 'GET') {
      return this._ok(this._store.insight(insight[1]));
    }
    const activity = path.match(/^\/activities\/([^/]+)$/);
    if (activity) {
      const id = activity[1];
      if (method === 'GET') return this._ok(this._store.find(id));
      if (method === 'PATCH') {
        return this._ok(
          this._store.update(id, req.body as Partial<CreateActivityPayload>),
          350,
        );
      }
      if (method === 'DELETE') {
        return this._ok({ deleted: this._store.remove(id) }, 350);
      }
    }

    // ── crawler: REAL, via the public no-auth endpoints ──
    if ((path === '/crawl' || path === '/demo/crawl') && method === 'POST') {
      const url = String(body?.['url'] ?? '');
      // The authed /crawl becomes the public /demo/crawl — same crawler,
      // no token needed. Falls back to a simulated draft if unreachable.
      const publicReq = req.clone({
        url: `${environment.apiBaseUrl}/demo/crawl`,
      });
      return next.handle(publicReq).pipe(
        catchError(() => this._ok(simulateCrawlDraft(url), 1200)),
      );
    }
    if (
      (path === '/demo/suggest-metrics' || path === '/demo/suggest-tags') &&
      method === 'POST'
    ) {
      // Public + stateless — use the real suggesters, fall back locally.
      return next.handle(req).pipe(
        catchError(() =>
          path === '/demo/suggest-metrics'
            ? this._ok({
                metrics: simulateCrawlDraft(String(body?.['url'] ?? '')) //
                  .metrics.map((m) => m.label),
              })
            : this._ok({
                tags: this._store.suggestTags(
                  String(body?.['title'] ?? ''),
                  body?.['type'] as string | undefined,
                ),
              }),
        ),
      );
    }
    if (path === '/sources' && method === 'GET') {
      return next
        .handle(req)
        .pipe(catchError(() => this._ok(this._store.sources)));
    }

    // ── account creation: ALWAYS real ──
    // Signing up from inside the demo is a conversion, not demo data — let
    // /auth/* (signup, resend-verification) through so real accounts work
    // even while the demo flag is still set.
    if (path.startsWith('/auth/')) {
      return next.handle(req);
    }
    if (path === '/crawl/save' && method === 'POST') {
      const draft = simulateCrawlDraft(String(body?.['url'] ?? ''));
      return this._ok(
        this._store.create({
          title: draft.title ?? 'Untitled activity',
          type: draft.type ?? 'other',
          date: draft.date ?? new Date().toISOString(),
          url: draft.url,
          source: draft.source,
          metrics: draft.metrics,
          fetched_via: draft.fetched_via,
          fetch_duration_ms: draft.fetch_duration_ms,
        }),
        1500,
      );
    }
    const refresh = path.match(/^\/refresh\/([^/]+)$/);
    if (refresh && method === 'POST') {
      const refreshed = this._store.refresh(refresh[1]);
      return this._ok({ refreshed: !!refreshed, activity: refreshed }, 900);
    }

    // ── profile ──
    if (path === '/profile' && method === 'GET') {
      return this._ok(this._store.profile);
    }
    if (path === '/profile' && method === 'PATCH') {
      return this._ok(this._store.updateProfile(req.body ?? {}), 350);
    }
    if (path === '/profile/change-password' && method === 'POST') {
      return this._ok({ updated: true }, 500);
    }

    // ── misc ──
    if (path === '/geocode' && method === 'GET') {
      return this._ok(this._store.geocode(req.params.get('q') ?? ''));
    }
    if (path === '/billing/status') {
      return this._ok({ premium: true, billing_configured: false });
    }
    if (path === '/billing/checkout') {
      return this._ok({ url: '' });
    }

    // Anything unrecognized gets an empty 200 — never let a demo call escape.
    return this._ok({});
  }
}
